import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../config';
import axios from 'axios';
import './MountainResume.css';

function MountainResume() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getCandidateIdFromSlug = (s) => {
    if (!s) return null;
    const parts = s.replace(/-resume$/, '').split('-');
    const id = parts[parts.length - 1];
    return isNaN(id) ? null : id;
  };

  const candidateId = getCandidateIdFromSlug(slug);

  const fetchResume = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/candidates/${candidateId}/resume`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      if (data.mountain_resume_url && data.mountain_resume_url !== slug) {
        setError('Resume not found'); setLoading(false); return;
      }
      setCandidate(data);
      setNeedsPassword(false);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) { setNeedsPassword(true); setLoading(false); }
      else if (err.response?.status === 404) { setError('Resume not found'); setLoading(false); }
      else if (err.response?.status === 403 && err.response?.data?.hold) { setError(err.response.data.error); setLoading(false); }
      else { setError('Error loading resume'); setLoading(false); }
    }
  };

  useEffect(() => {
    if (!candidateId) { setError('Invalid resume URL'); setLoading(false); return; }
    const token = localStorage.getItem('token');
    if (!token) { setNeedsPassword(true); setLoading(false); return; }
    fetchResume(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPasswordError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/resume-password-login`, {
        candidateId, password: passwordInput
      });
      localStorage.setItem('token', res.data.token);
      setLoading(true);
      await fetchResume(res.data.token);
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Invalid password');
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingColor = (r) => !r ? '#6b7280' : r >= 8 ? '#16a34a' : r >= 5 ? '#d97706' : '#dc2626';
  const getRatingLabel = (r) => !r ? 'N/A' : r >= 8 ? 'Strong Fit' : r >= 5 ? 'Good Fit' : 'Needs Review';

  const formatPhone = (phone) => {
    if (!phone) return '';
    const d = phone.replace(/\D/g, '');
    return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : phone;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
    catch { return dateStr; }
  };

  if (loading) return <div className="mr-loading"><div className="mr-spinner"/><p>Loading resume...</p></div>;
  if (error) return <div className="mr-error"><h2>{/unlock/.test(error) ? 'Almost there' : 'Resume Not Found'}</h2><p>{error}</p><button onClick={() => navigate(-1)}>Go Back</button></div>;
  if (needsPassword) {
    return (
      <div className="mr-password-gate">
        <div className="mr-password-box">
          <div className="mr-brand">⛰ Mountain Recruiting</div>
          <h2>Enter Your Password</h2>
          <p className="mr-password-hint">Just the password, no need to enter your email.</p>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <p className="mr-password-error">{passwordError}</p>}
            <button type="submit" disabled={submitting || !passwordInput}>
              {submitting ? 'Checking…' : 'View Resume'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const p = candidate.candidate_profile || {};
  const extracted = candidate.resumeData?.extracted || {};
  const rating = candidate.mountain_rating;

  const displaySummary = candidate.experience_summary || extracted.experience_summary;
  const displayLocation = candidate.location || (p.location ? [p.location.city, p.location.state, p.location.zip].filter(Boolean).join(', ') : null) || extracted.location;
  const tradeTitle = p.trade_types?.length ? p.trade_types[0] : 'Candidate';
  const hasProfile = candidate.candidate_profile !== null && candidate.candidate_profile !== undefined;

  const quickFitStatus = (status) => {
    if (status === 'pass') return 'mr-fit-pass';
    if (status === 'warn') return 'mr-fit-warn';
    return 'mr-fit-fail';
  };

  const quickFitIcon = (status) => status === 'pass' ? '✓' : status === 'warn' ? '⚠' : '✗';

  const relTagClass = (tag) => {
    const map = { Driving: 'rel-driving', Delivery: 'rel-delivery', Physical: 'rel-physical', Warehouse: 'rel-warehouse', 'Tech/App': 'rel-tech', 'Customer Service': 'rel-customer', Supervisory: 'rel-supervisory', Trades: 'rel-trades', 'Food Service': 'rel-food', Other: 'rel-other' };
    return map[tag] || 'rel-other';
  };

  const screeningBadge = (label, value) => {
    if (!value) return null;
    const v = value.toLowerCase();
    const cls = (v === 'pass' || v === 'complete') ? 'mr-screen-pass' : v === 'pending' ? 'mr-screen-pending' : 'mr-screen-fail';
    return <span key={label} className={`mr-screen-badge ${cls}`}>{label}: {value}</span>;
  };

  return (
    <div className="mr-page">
      <div className="mr-container">

        {/* HEADER */}
        <div className="mr-header">
          <div className="mr-header-left">
            <div className="mr-brand">⛰ Mountain Recruiting</div>
            <div className="mr-trade-title">{tradeTitle}</div>
            <h1 className="mr-name">{candidate.candidate_name}</h1>
          </div>
          <div className="mr-header-right">
            {rating && (
              <div className="mr-rating-badge" style={{ borderColor: getRatingColor(rating), color: getRatingColor(rating) }}>
                <span className="mr-rating-num">{rating}</span>
                <span className="mr-rating-label">{getRatingLabel(rating)}</span>
                <span className="mr-rating-sub">Mountain Rating</span>
              </div>
            )}
            <div className="mr-contact-info">
              {displayLocation && <div>📍 {displayLocation}</div>}
              {candidate.phone && <div>📞 <a href={`tel:${candidate.phone}`}>{formatPhone(candidate.phone)}</a></div>}
              {candidate.email && <div>✉ <a href={`mailto:${candidate.email}`}>{candidate.email}</a></div>}
              {candidate.screening_date && <div>📅 Screened: {formatDate(candidate.screening_date)}</div>}
            </div>
            <div className="mr-screen-badges">
              {screeningBadge('BGV', candidate.bg_status)}
              {screeningBadge('DOT', candidate.drug_status)}
              {screeningBadge('MEC', candidate.physical_status)}
            </div>
          </div>
        </div>

        {/* QUICK FIT BAR */}
        {p.quick_fit?.length > 0 && (
          <div className="mr-fit-bar">
            <span className="mr-fit-bar-label">Quick Fit:</span>
            {p.quick_fit.map((item, i) => (
              <span key={i} className={`mr-fit-tag ${quickFitStatus(item.status)}`}>
                {quickFitIcon(item.status)} {item.label}
              </span>
            ))}
          </div>
        )}

        {/* BODY */}
        <div className={`mr-body ${hasProfile ? '' : 'mr-body-simple'}`}>

          {/* SIDEBAR */}
          {hasProfile && (
            <div className="mr-sidebar">

              {/* License & Certs */}
              {p.certifications?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">License &amp; Certs</div>
                  {p.certifications.map((cert, i) => (
                    <div key={i} className="mr-cert-item">
                      <span className="mr-cert-icon">✓</span> {cert}
                    </div>
                  ))}
                </div>
              )}

              {/* Physical Readiness */}
              {p.physical_demands?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">Physical Readiness</div>
                  <ul className="mr-check-list">
                    {p.physical_demands.map((item, i) => (
                      <li key={i}><span className="mr-check">✓</span> {item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tech Comfort */}
              {p.tech_comfort?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">Tech Comfort</div>
                  <ul className="mr-check-list">
                    {p.tech_comfort.map((item, i) => (
                      <li key={i}><span className="mr-check">✓</span> {item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Equipment */}
              {p.equipment?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">Equipment &amp; Tools</div>
                  <div className="mr-skill-tags">
                    {p.equipment.map((item, i) => (
                      <span key={i} className="mr-skill-tag mr-skill-highlight">{item}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Vehicle Owned */}
              {p.vehicle_owned?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">Vehicle Owned</div>
                  <ul className="mr-check-list">
                    {p.vehicle_owned.map((item, i) => (
                      <li key={i}><span className="mr-check">✓</span> {item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Skills */}
              {p.soft_skills?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">Key Skills</div>
                  <div className="mr-skill-tags">
                    {p.soft_skills.map((item, i) => (
                      <span key={i} className="mr-skill-tag">{item}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {p.languages?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">Languages</div>
                  {p.languages.map((lang, i) => (
                    <div key={i} className="mr-lang-item">
                      <span>{lang.language}</span>
                      <div className="mr-lang-dots">
                        {[1,2,3,4,5].map(n => (
                          <div key={n} className={`mr-lang-dot ${n <= lang.level ? 'filled' : ''}`}/>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Availability */}
              {p.availability?.length > 0 && (
                <div className="mr-sidebar-section">
                  <div className="mr-sidebar-title">Availability</div>
                  <div className="mr-avail-grid">
                    {p.availability.map((item, i) => (
                      <div key={i} className="mr-avail-item">{item}</div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* MAIN CONTENT */}
          <div className="mr-main">

            {/* Summary */}
            {displaySummary && (
              <div className="mr-section">
                <div className="mr-section-title">Summary</div>
                <p className="mr-summary">{displaySummary}</p>
              </div>
            )}

            {/* Work Experience */}
            {p.work_experience?.length > 0 && (
              <div className="mr-section">
                <div className="mr-section-title">Work Experience</div>
                {p.work_experience.map((job, i) => (
                  <div key={i} className="mr-job">
                    <div className="mr-job-header">
                      <div>
                        <div className="mr-job-title">{job.title}</div>
                        <div className="mr-job-company">{job.company}</div>
                      </div>
                      {job.dates && <div className="mr-job-date">{job.dates}</div>}
                    </div>
                    {job.relevance_tags?.length > 0 && (
                      <div className="mr-job-tags">
                        {job.relevance_tags.map((tag, j) => (
                          <span key={j} className={`mr-rel-tag ${relTagClass(tag)}`}>{tag}</span>
                        ))}
                      </div>
                    )}
                    {job.bullets?.length > 0 && (
                      <ul className="mr-job-bullets">
                        {job.bullets.map((b, j) => <li key={j}>{b}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Education */}
            {p.education?.length > 0 && (
              <div className="mr-section">
                <div className="mr-section-title">Education &amp; Training</div>
                {p.education.map((item, i) => (
                  <div key={i} className="mr-edu-item">✓ {item}</div>
                ))}
              </div>
            )}

            {/* Additional Qualifications */}
            {p.additional_qualifications?.length > 0 && (
              <div className="mr-section">
                <div className="mr-section-title">Additional Qualifications</div>
                <ul className="mr-check-list mr-check-list-main">
                  {p.additional_qualifications.map((item, i) => (
                    <li key={i}><span className="mr-check">✓</span> {item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fallback for non-AI resumes */}
            {!hasProfile && candidate.last_worked && (
              <div className="mr-section">
                <div className="mr-section-title">Most Recent Employer</div>
                <p className="mr-summary">{candidate.last_worked}</p>
              </div>
            )}

            {candidate.is_ai_processed && (
              <div className="mr-ai-badge">
                <span>🤖 AI-Processed Resume</span>
                <span className="mr-ai-sub">Extracted &amp; rated by Mountain Recruiting AI</span>
              </div>
            )}
          </div>
        </div>

        <div className="mr-footer">
          <p>Prepared by Mountain Recruiting · mountainrecruiting.com</p>
          <p>Confidential — for client use only</p>
        </div>
      </div>
    </div>
  );
}

export default MountainResume;
